import React, { useEffect } from 'react';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import SharedButton from '../../components/Shared/SharedButton';
import DropdownComponent from '../../components/Shared/DropdownComponent';
import TextInput from '../../components/Shared/TextInput';
import PageHeader from '../../components/Shared/PageHeader';
import DatePicker from '../../components/Shared/DatePicker';
import { FaCheckDouble } from 'react-icons/fa6';
import CustomSearchDropdown from '../../components/Shared/CustomSearchDropdown';
import { getAllEmployees } from '../../store/slices/EmployeeSlice';
import { CreateNewDeduction, UpdateDeduction } from '../../store/slices/HrSlice';

export default function CreateDeduction() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user } = useSelector(state => state.auth);
  const { isLoading } = useSelector(state => state.employee);
  const { isLoading: loading } = useSelector(state => state?.hr);
  const { allEmployees } = useSelector(state => state.employee);

  const listEmployees = allEmployees?.data
    ? allEmployees.data.map(employee => ({
      id: employee.id,
      name: `${employee.first_name} ${employee.last_name}`
    }))
    : [];

  useEffect(() => {
    dispatch(getAllEmployees());
  }, [dispatch]);

  const location = useLocation();
  const { editingDeduction } = location.state || {};

  const initialValues = {
    // id: editingDeduction?.id || '',
    employee_id: editingDeduction?.employee_id || '',
    deduction_type: editingDeduction?.deduction_type || '',
    description: editingDeduction?.description || '',
    amount: editingDeduction?.amount || 0,
    effective_date: editingDeduction?.effective_date ? new Date(editingDeduction.effective_date) : null,
    branch_id: editingDeduction?.branch_id || user?.branch_id,
    business_id: editingDeduction?.business_id || user?.business_id
  };

  const validationSchema = Yup.object({
    employee_id: Yup.number().required(t('required')),
    deduction_type: Yup.string().required(t('required')),
    description: Yup.string().required(t('required')),
    amount: Yup.number().min(0, `${t('amount')}${t('mustBePositive')}`),
    effective_date: Yup.date().required(t('required'))
  });

  const deduction_type_option = [
    { value: 'Tax', label: 'Tax' },
    { value: 'Absent', label: 'Absent' },
    { value: 'Advance', label: 'Advance' }
  ];

  const onSubmit = async (values, { resetForm }) => {
    try {
      const formattedData = {
        ...values,
        effective_date: values.effective_date ? new Date(values.effective_date).toISOString().split('T')[0] : '',
      };

      const resultAction = editingDeduction?.id
        ? await dispatch(UpdateDeduction({ ...formattedData, id: editingDeduction?.id }))
        : await dispatch(CreateNewDeduction(formattedData));

      if (UpdateDeduction.fulfilled.match(resultAction) || CreateNewDeduction.fulfilled.match(resultAction)) {
        resetForm();
        navigate(-1);
      }
    } catch (error) {
      console.error('Error submitting deduction:', error);
    }
  };

  return (
    <>
      <PageHeader
        title={t('Create Deduction')}
        showBack
        onBack={() => navigate(-1)}
      />

      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit} className="space-y-4">
            <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 w-full'>
              <CustomSearchDropdown
                name='employee_id'
                placeholder={`${t('select')} ${t('employee')}`}
                options={listEmployees}
                label={`${t('employee')} ${t('name')}`}
                value={formik.values.employee_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={'!w-full !border-gray-300 !bg-gray-100 !h-[30px]'}
              />

              <DropdownComponent
                label={t('deduction_type')}
                name={'deduction_type'}
                placeholder={`${t('select')} ${t('deduction_type')}`}
                options={deduction_type_option}
                className='!w-full'
              />

              <DatePicker
                label={t('effective_date')}
                name={'effective_date'}
                placeholder={`${t('select')} ${t('effective_date')}`}
                className='!w-full'
              />

              <TextInput
                type={'number'}
                label={t('amount')}
                name={'amount'}
                placeholder={`${t('enter')} ${t('amount')}`}
                className='!p-1.5'
              />

              <TextInput
                label={t('description')}
                name={'description'}
                placeholder={`${t('enter')} ${t('description')}`}
                className='!p-1.5'
              />
            </div>

            <div className='flex justify-end gap-2 '>
              <SharedButton
                type='button'
                icon='pi pi-times'
                label={t('Cancel')}
                disabled={loading}
                buttonType='reset'
                className='cancel-btn'
                onClick={() => {
                  navigate(-1);
                }}
              />
              <SharedButton
                type='submit'
                icon={<FaCheckDouble size={12} className='me-2' />}
                loading={loading}
                disabled={loading}
                label={t('Submit')}
                className='submit-btn'
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}